import { KeyIcon } from '@synoptic/ui-kit/icons/react/key.tsx';
import { LockSlashIcon } from '@synoptic/ui-kit/icons/react/lock-slash.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC, ReactNode } from 'react';
import {
  PackageInStreamFragmentFragment,
  StripeBillingInterval,
  StreamState,
  StreamType,
  UserBadgeFragmentFragment,
} from '@/__generated__/graphql.ts';
import { S3Avatar } from '@/components/s3/user/s3-avatar.tsx';
import {
  apiAccessLabel,
  apiAccessLabelDisabled,
  avatarLink,
  bannerHeader,
  createdBy,
  createdByContainer,
  headerContent,
  streamAuthorLink,
  verticalSeparator,
} from '@/components/streams/stream-banner/banner-header.css.ts';
import { StreamPriceLabel } from '@/components/streams/stream-tag/stream-price-label.tsx';
import { StreamStateTag } from '@/components/streams/stream-tag/stream-state-tag.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { IncludedInPackageLabel } from '@/routes/_app.p.streams_.$id/stream-banner/included-in-package-label.tsx';

export const BannerHeader: FC<{
  author: UserBadgeFragmentFragment;
  price?: number;
  activeSubscription: boolean;
  customStream?: boolean;
  packageOnly?: boolean;
  packages?: PackageInStreamFragmentFragment[];
  isAuthor: boolean;
  actionsMenu: ReactNode;
  streamState?: StreamState;
  streamType?: StreamType;
  createdByPrefix: string;
  interval?: StripeBillingInterval;
  intervalCount?: number;
  apiAccessEnabled?: boolean;
}> = ({
  author,
  price,
  activeSubscription,
  customStream,
  packageOnly,
  packages,
  isAuthor,
  streamState,
  streamType,
  actionsMenu,
  createdByPrefix,
  interval,
  intervalCount,
  apiAccessEnabled,
}) => {
  const isTablet = useIsTablet();

  const showPrice = isTablet && price && (isAuthor || activeSubscription);
  const showPackages =
    isTablet && packages?.length && (isAuthor || activeSubscription);
  const showStateTag = streamState && streamType;

  return (
    <div className={bannerHeader}>
      <div className={headerContent}>
        {showStateTag ? (
          <>
            {streamState === StreamState.Draft ||
            streamState === StreamState.OnHold ? (
              <StreamStateTag type={streamType} />
            ) : null}
            <StreamStateTag state={streamState} type={streamType} />
          </>
        ) : null}
        <CreatedBy author={author} prefix={createdByPrefix} />
        {showPrice ? (
          <>
            <div className={verticalSeparator} />
            <StreamPriceLabel
              variant={'secondary'}
              price={price}
              subscribed={!isAuthor}
              isStartingPrice={isAuthor && customStream}
              interval={interval}
              intervalCount={intervalCount}
            />
          </>
        ) : null}
        {showPackages ? (
          <>
            <div className={verticalSeparator} />
            <IncludedInPackageLabel
              packages={packages}
              packageOnly={packageOnly}
            />
          </>
        ) : null}
        {isTablet && apiAccessEnabled !== undefined ? (
          <>
            <div className={verticalSeparator} />
            {apiAccessEnabled ? (
              <span
                className={apiAccessLabel}
                title="External API consumers can subscribe to this stream"
              >
                <KeyIcon width={14} height={14} aria-hidden />
                API access
              </span>
            ) : (
              <span
                className={apiAccessLabelDisabled}
                title="External API consumers cannot subscribe to this stream"
              >
                <LockSlashIcon width={14} height={14} aria-hidden />
                API access disabled
              </span>
            )}
          </>
        ) : null}
      </div>
      {actionsMenu}
    </div>
  );
};

export const CreatedBy: FC<{
  author: UserBadgeFragmentFragment;
  prefix: string;
}> = ({ author, prefix }) => {
  const isTablet = useIsTablet();

  return (
    <Link
      className={createdByContainer}
      to={paths.makeUser(author.username)}
      title={`Hosted by ${author.username}`}
    >
      <div className={avatarLink}>
        <S3Avatar size={'xs'} s3Key={author.avatar?.key} userId={author.id} />
      </div>
      <span className={createdBy}>
        {isTablet ? prefix + ' hosted by ' : 'Hosted by '}
        <span className={streamAuthorLink}>@{author.username}</span>
      </span>
    </Link>
  );
};
